import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getPageTitleSizeClass, getSectionHeadingSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';
import { CATEGORY_INFO, type CategoryInfo } from '../../components/CategoryInfoModal/CategoryInfoModal';

/** Renders calculation text with clearer formatting: formula lines (containing " = ") as distinct rows, other lines as paragraphs. */
function CalculationBlock({ text, textSizeClass }: { text: string; textSizeClass: string }) {
  const lines = text.split(/\n/).map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-4">
      {lines.map((line, i) => {
        const eqIndex = line.indexOf(' = ');
        const looksLikeFormula = eqIndex > 0 && eqIndex < line.length - 3;
        if (looksLikeFormula) {
          const label = line.slice(0, eqIndex);
          const formula = line.slice(eqIndex + 3);
          return (
            <div
              key={i}
              className="flex flex-col gap-1 rounded-lg bg-gray-100/80 dark:bg-gray-800/60 px-4 py-3 border border-gray-200/80 dark:border-gray-700/60 sm:flex-row sm:items-baseline sm:gap-3"
            >
              {/* Label and separator stay on one line; formula wraps at word boundaries only */}
              <span className={`${textSizeClass} font-medium text-text-light-primary dark:text-text-primary whitespace-nowrap flex-shrink-0`}>
                {label}
                <span className="text-text-light-secondary dark:text-text-secondary font-normal">: </span>
              </span>
              <span className={`${textSizeClass} font-mono text-text-light-secondary dark:text-text-secondary break-words min-w-0`}>
                {formula}
              </span>
            </div>
          );
        }
        return (
          <p key={i} className={`${textSizeClass} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
            {line}
          </p>
        );
      })}
    </div>
  );
}

interface TechnicalArticleConfig {
  slug: string;
  categoryName: string;
  pageTitle: string;
  introLabel: string;
}

const TECHNICAL_ARTICLES: TechnicalArticleConfig[] = [
  {
    slug: 'range_analysis',
    categoryName: 'Range Analysis',
    pageTitle: 'Range Analysis Technical Indicator Tutorial',
    introLabel: 'Range Analysis',
  },
  {
    slug: 'simple_moving_average',
    categoryName: 'Simple Moving Average',
    pageTitle: 'Simple Moving Average (SMA) Technical Indicator Tutorial',
    introLabel: 'Simple Moving Average (SMA)',
  },
  {
    slug: 'volume_moving_average',
    categoryName: 'Volume Moving Average',
    pageTitle: 'Volume Moving Average (VMA) Technical Indicator Tutorial',
    introLabel: 'Volume Moving Average (VMA)',
  },
  {
    slug: 'exponential_moving_average',
    categoryName: 'Exponential Moving Average',
    pageTitle: 'Exponential Moving Average (EMA) Technical Indicator Tutorial',
    introLabel: 'Exponential Moving Average (EMA)',
  },
  {
    slug: 'relative_strength_index',
    categoryName: 'Relative Strength Index',
    pageTitle: 'Relative Strength Index (RSI) Technical Indicator Tutorial',
    introLabel: 'Relative Strength Index (RSI)',
  },
  {
    slug: 'macd',
    categoryName: 'MACD',
    pageTitle: 'MACD Technical Indicator Tutorial',
    introLabel: 'Moving Average Convergence Divergence (MACD)',
  },
  {
    slug: 'stochastic_oscillator',
    categoryName: 'Stochastic Oscillator',
    pageTitle: 'Stochastic Oscillator Technical Indicator Tutorial',
    introLabel: 'Stochastic Oscillator',
  },
  {
    slug: 'bollinger_bands',
    categoryName: 'Bollinger Bands',
    pageTitle: 'Bollinger Bands Technical Indicator Tutorial',
    introLabel: 'Bollinger Bands',
  },
  {
    slug: 'fibonacci_levels',
    categoryName: 'Fibonacci Levels',
    pageTitle: 'Fibonacci Levels Technical Indicator Tutorial',
    introLabel: 'Fibonacci Levels',
  },
];

function buildStockTableDisplayCopy(categoryName: string, info: CategoryInfo): string {
  switch (categoryName) {
    case 'Range Analysis':
      return 'Stock Table groups 52-week highs, lows, and distance from each into a single Range Analysis section. Colors highlight where today’s price sits in that range so you can instantly see whether a stock is trading near support, near resistance, or comfortably in the middle of its recent history.';
    case 'Simple Moving Average':
      return 'Stock Table lines up each Simple Moving Average next to the next longer timeframe and uses green and red coloring to show whether the shorter trend is above or below the longer trend. This makes it easy to scan a table and see which tickers have strong trend alignment and which ones are starting to break down.';
    case 'Volume Moving Average':
      return 'For Volume Moving Averages, Stock Table combines volume trends with matching price SMAs. When both price and volume move in the same constructive direction, you see it immediately, helping you focus on moves that have meaningful participation instead of noise. When the price is trending higher with higher volume, the volume would be shown in green; if the price is trending lower with higher volume, the volume would be shown in red.';
    case 'Exponential Moving Average':
      return 'Exponential Moving Averages in Stock Table are arranged from fast to slow, with colors and crossover icons that emphasize when shorter EMAs flip above or below longer EMAs. This helps you quickly spot fresh trend changes without scrolling through separate charts for every ticker.';
    case 'Relative Strength Index':
      return 'Stock Table displays multiple RSI lookbacks together and uses clear thresholds and icons to flag overbought and oversold conditions. You can quickly see which tickers have stretched momentum and which are recovering from extreme levels, all within the same table view.';
    case 'MACD':
      return 'Within Stock Table, MACD values and their zero-line relationship are surfaced directly in the table so you can see when momentum is turning positive or negative. By comparing MACD across many tickers at once, you can quickly narrow your focus to names with strengthening or weakening trends.';
    case 'Stochastic Oscillator':
      return 'Stock Table presents %K and %D Stochastic values side-by-side and highlights when momentum is extended or crossing. This layout makes it straightforward to scan for potential short-term reversal candidates across a list instead of checking one chart at a time.';
    case 'Bollinger Bands':
      return 'Bollinger Bands in Stock Table show where price sits relative to the upper, middle, and lower bands and use icons to flag squeezes, breakouts, and breakdowns. This helps you quickly find tickers where volatility is compressing or where price is pushing into potential support or resistance.';
    case 'Fibonacci Levels':
      return 'Stock Table automatically calculates the most recent qualifying price swing and maps each Fibonacci retracement level for you. Visual cues show whether price is respecting or breaking those levels, allowing you to compare potential pullback zones for many stocks in a single table.';
    default:
      return info.coloringLogic || 'Stock Table displays this indicator alongside related metrics and uses intuitive coloring and icons so you can interpret the signal quickly without manual calculations.';
  }
}

const TechnicalIndicatorArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { fontSize } = useFontSize();

  const config = TECHNICAL_ARTICLES.find((item) => item.slug === slug);
  const info = config ? CATEGORY_INFO[config.categoryName] : undefined;

  useEffect(() => {
    if (config) {
      document.title = `Stock Table Tutorials - ${config.pageTitle}`;
    } else {
      document.title = 'Stock Table Tutorials - Technical Indicators';
    }
    window.scrollTo(0, 0);
  }, [config]);

  if (!config || !info) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
        <Header />
        <main className="flex-grow max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8">
            <h1 className={`${getPageTitleSizeClass(fontSize)} font-bold text-text-light-primary dark:text-text-primary mb-3`}>
              Technical Indicator Not Found
            </h1>
            <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary mb-6`}>
              We could not find a tutorial for this technical indicator.
            </p>
            <Link
              to="/tutorials/technicals"
              className={`inline-block px-5 py-2 rounded-lg bg-primary dark:bg-highlight-gold text-gray-900 font-medium hover:opacity-90 transition-opacity ${getTextSizeClass(fontSize)}`}
            >
              View all technical indicator tutorials
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const stockTableDisplayCopy = buildStockTableDisplayCopy(config.categoryName, info);

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      <Header />

      <main className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <article className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8 md:p-10">
          <header className="mb-8">
            <h1 className={`${getPageTitleSizeClass(fontSize)} font-bold text-text-light-primary dark:text-text-primary`}>
              {config.introLabel}
            </h1>
          </header>

          <div className="prose prose-sm sm:prose-base max-w-none text-text-light-primary dark:text-text-primary">
            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3`}>
                What is {info.title}?
              </h2>
              <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                {info.description}
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3`}>
                How is it calculated?
              </h2>
              <CalculationBlock text={info.calculation} textSizeClass={getTextSizeClass(fontSize)} />
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3`}>
                Trading Context
              </h2>
              <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                {info.context}
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3`}>
                Limitations to Consider
              </h2>
              <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                {info.limitations}
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3`}>
                Additional Insights
              </h2>
              <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                {info.additional ||
                  'This indicator becomes even more powerful when combined with complementary technical or fundamental signals. Consider using it alongside other tools in Stock Table to confirm your trading thesis.'}
              </p>
            </section>

            <section className="mb-10">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3`}>
                How Stock Table displays {info.title}
              </h2>
              <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                {stockTableDisplayCopy}
              </p>
              {config.slug === 'range_analysis' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/range-analysis-example.png"
                    alt="Range Analysis indicators as shown in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'simple_moving_average' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/simple-moving-average-example.png"
                    alt="Simple Moving Average indicators as shown in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'volume_moving_average' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/volume-moving-average-example.png"
                    alt="Volume Moving Average indicators as shown in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'exponential_moving_average' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/exponential-moving-average-example.png"
                    alt="Exponential Moving Average indicators as shown in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'relative_strength_index' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/rsi-example.png"
                    alt="Relative Strength Index indicators as shown in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'macd' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/macd-example.png"
                    alt="MACD indicators as shown in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'stochastic_oscillator' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/stochastic-oscillator-example.png"
                    alt="Stochastic Oscillator indicators as shown in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'bollinger_bands' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/bollinger-bands-example.png"
                    alt="Bollinger Bands indicators as shown in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'fibonacci_levels' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/fibonacci-levels-example.png"
                    alt="Fibonacci Levels indicators as shown in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
            </section>

            <div className="pt-6 border-t border-border-light dark:border-border-color">
              <Link
                to="/tutorials/technicals"
                className={`inline-block px-5 py-2 rounded-lg bg-primary dark:bg-highlight-gold text-gray-900 font-medium hover:opacity-90 transition-opacity ${getTextSizeClass(fontSize)}`}
              >
                Back to technical tutorials
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default TechnicalIndicatorArticle;

